import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Database, ShieldCheck, Loader2, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

/**
 * Admin-only card on the Integrations page.
 *
 * Lets an admin run a DRY-RUN audit of every resource's `category` field
 * against the canonical slug taxonomy, then apply the safe fixes
 * (label_fix + approved_fix) without touching `needs_decision` or
 * `missing` rows.
 *
 * The backend endpoints (`/admin/resources/category-audit` and
 * `/admin/resources/category-cleanup`) are already RBAC-gated; this
 * component just wraps them in a clear two-step UI so admins don't have
 * to curl from the command line.
 */
export default function ResourceCategoryCleanupCard() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastApplyResult, setLastApplyResult] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setLastApplyResult(null);
    try {
      const { data } = await api.get("/admin/resources/category-audit");
      setAudit(data);
      const s = data.summary || {};
      const fixable = (s.label_fix || 0) + (s.approved_fix || 0);
      if (fixable === 0 && (s.needs_decision || 0) === 0 && (s.missing || 0) === 0) {
        toast.success("All resources already use canonical category slugs — nothing to clean up.");
      } else {
        toast.success(`Audit complete · ${fixable} row${fixable === 1 ? "" : "s"} can be auto-fixed.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const applyCleanup = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/resources/category-cleanup?confirm=APPLY");
      setLastApplyResult(data);
      toast.success(`Cleanup applied · ${data.applied} row${data.applied === 1 ? "" : "s"} normalized.`);
      // Re-run audit so the UI reflects the new state.
      await runAudit();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
      setConfirmOpen(false);
    }
  };

  const summary = audit?.summary;
  const labelFix = audit?.buckets?.label_fix || [];
  const approvedFix = audit?.buckets?.approved_fix || [];
  const needsDecision = audit?.buckets?.needs_decision || [];
  const missing = audit?.buckets?.missing || [];
  const totalFixable = (summary?.label_fix || 0) + (summary?.approved_fix || 0);

  return (
    <div
      className="bg-white border border-[#E5E5E0] rounded-2xl p-6"
      data-testid="resource-category-cleanup-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] text-[#1E3A8A] flex items-center justify-center">
            <Database className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">
              Resource categories — audit &amp; cleanup
            </h2>
            <p className="text-xs text-[#1E3A8A] mt-0.5 max-w-2xl">
              Normalize legacy <code className="font-mono">category</code> values (case differences,
              human labels, approved legacy aliases) to the canonical slug taxonomy so filtered
              and search views find every resource.
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#1E3A8A] font-semibold">
                <ShieldCheck className="w-3 h-3" /> Admin only · dry-run by default
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runAudit}
            disabled={loading}
            data-testid="run-category-audit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {audit ? "Re-run audit" : "Run audit"}
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!audit || totalFixable === 0 || applying}
            data-testid="apply-category-cleanup"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1E3A8A] text-[#1E3A8A] text-sm font-medium hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title={!audit ? "Run the audit first" : totalFixable === 0 ? "Nothing to apply" : ""}
          >
            Apply cleanup{totalFixable > 0 ? ` (${totalFixable})` : ""}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2" data-testid="category-audit-summary">
          <SummaryStat label="Canonical" value={summary.already_canonical} accent="text-[#166534]" />
          <SummaryStat label="Label-fix" value={summary.label_fix} accent="text-[#1E3A8A]" />
          <SummaryStat label="Approved-fix" value={summary.approved_fix} accent="text-[#1E3A8A]" />
          <SummaryStat label="Needs decision" value={summary.needs_decision} accent="text-[#92400E]" />
          <SummaryStat label="Missing" value={summary.missing} accent="text-[#991B1B]" />
        </div>
      )}

      {/* Last apply result */}
      {lastApplyResult && (
        <div className="mt-4 rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-3" data-testid="cleanup-result-banner">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#166534] mt-0.5" />
            <div className="text-xs text-[#065F46]">
              <strong>{lastApplyResult.applied}</strong> row{lastApplyResult.applied === 1 ? "" : "s"} normalized.
              {lastApplyResult.remaining?.length > 0 && (
                <> {lastApplyResult.remaining.length} row{lastApplyResult.remaining.length === 1 ? "" : "s"} still need your attention (unknown or missing category) — see below.</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail tables */}
      {audit && totalFixable > 0 && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {labelFix.length > 0 && (
            <CategoryRowList
              testId="audit-bucket-label-fix"
              title={`Label-fix — case / whitespace / readable label (${labelFix.length})`}
              tone="info"
              rows={labelFix}
            />
          )}
          {approvedFix.length > 0 && (
            <CategoryRowList
              testId="audit-bucket-approved-fix"
              title={`Approved-fix — legacy alias (${approvedFix.length})`}
              tone="info"
              rows={approvedFix}
            />
          )}
        </div>
      )}

      {audit && (needsDecision.length > 0 || missing.length > 0) && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {needsDecision.length > 0 && (
            <CategoryRowList
              testId="audit-bucket-needs-decision"
              title={`Needs decision — unknown values (${needsDecision.length})`}
              tone="warn"
              rows={needsDecision}
              note="These rows will NOT be auto-modified. Edit them by hand from the Resource Directory."
            />
          )}
          {missing.length > 0 && (
            <CategoryRowList
              testId="audit-bucket-missing"
              title={`Missing category (${missing.length})`}
              tone="warn"
              rows={missing}
              note="Empty or null category. Edit these resources to assign a category."
            />
          )}
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="cleanup-confirm-modal"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-[#92400E]" />
              <div className="flex-1">
                <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                  Apply category cleanup?
                </h3>
                <p className="text-xs text-[#334155]">
                  This will rewrite {totalFixable} row{totalFixable === 1 ? "" : "s"} in the live
                  resource directory. Unknown and missing categories will be left alone.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              {labelFix.length > 0 && (
                <ConfirmList
                  title={`Label fixes (${labelFix.length})`}
                  rows={labelFix}
                />
              )}
              {approvedFix.length > 0 && (
                <ConfirmList
                  title={`Approved alias fixes (${approvedFix.length})`}
                  rows={approvedFix}
                />
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E0] flex items-center justify-end gap-2 bg-[#F9F8F6]">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={applying}
                data-testid="cleanup-confirm-cancel"
                className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={applyCleanup}
                disabled={applying}
                data-testid="cleanup-confirm-apply"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-40"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Yes, apply {totalFixable} fix{totalFixable === 1 ? "" : "es"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, accent }) {
  return (
    <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#334155]">{label}</div>
      <div className={`font-display text-2xl font-semibold ${accent || "text-[#0F172A]"}`}>{value ?? 0}</div>
    </div>
  );
}

function CategoryRowList({ title, tone, rows, note, testId }) {
  const toneStyles = tone === "warn"
    ? "border-[#FDE68A] bg-[#FFFBEB]"
    : "border-[#E5E5E0] bg-white";
  return (
    <div className={`rounded-xl border p-3 ${toneStyles}`} data-testid={testId}>
      <div className="text-xs font-semibold text-[#0F172A] mb-2">{title}</div>
      {note && <p className="text-[11px] text-[#92400E] mb-2">{note}</p>}
      <ul className="space-y-1 text-xs max-h-48 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 border-b border-[#F0EFEB] last:border-b-0 py-1">
            <span className="truncate text-[#0F172A]" title={r.name}>{r.name}</span>
            <span className="font-mono text-[10px] text-[#334155] whitespace-nowrap">
              {JSON.stringify(r.current)}{r.proposed ? ` → ${r.proposed}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConfirmList({ title, rows }) {
  return (
    <div>
      <div className="text-xs font-semibold text-[#0F172A] mb-1">{title}</div>
      <ul className="space-y-0.5 text-xs max-h-40 overflow-y-auto bg-[#F9F8F6] rounded-lg p-2">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2">
            <span className="truncate text-[#0F172A]" title={r.name}>{r.name}</span>
            <span className="font-mono text-[10px] text-[#334155] whitespace-nowrap">
              {JSON.stringify(r.current)} → {r.proposed}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
